
import { motion } from 'framer-motion';

export default function CTASection() {
  return (
    <section className="relative py-8 bg-gray-50 overflow-hidden">
      {/* Conteneur principal réduit */}
      <div className="max-w-5xl mx-auto px-4">
        {/* Bulle principale plus petite */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-4 sm:p-4 md:p-6 shadow-2xl overflow-hidden"
        >
          {/* Effets lumineux subtils - Static on mobile */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-2xl"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-teal-500/10 rounded-full blur-2xl"></div>
          
          {/* Contenu principal en grid compact */}
          <div className="relative z-10 grid lg:grid-cols-2 gap-2 sm:gap-4 items-center">
            
            {/* Colonne gauche - Texte */}
            <div className="text-left">
              {/* Badge offre limitée plus petit */}
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center space-x-2 text-[10px] sm:text-xs font-bold text-emerald-400 tracking-wider uppercase mb-2 sm:mb-3 px-2 sm:px-3 py-1 bg-emerald-400/10 backdrop-blur-sm rounded-full border border-emerald-400/20"
              >
                <i className="ri-flashlight-line text-xs sm:text-sm"></i>
                <span>Offre Limitée</span>
              </motion.div>

              {/* Titre principal plus compact */}
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black leading-tight mb-2 sm:mb-3"
              >
                <span className="text-white block">
                  Prêt à Réduire Vos Coûts
                </span>
                <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent block">
                  d'Énergie de 75% ?
                </span>
              </motion.h2>

              {/* Description plus courte */}
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-gray-300 text-xs sm:text-sm md:text-base mb-3 sm:mb-4 lg:mb-0 leading-relaxed"
              >
                Obtenez votre étude gratuite en 24h et découvrez combien vous pouvez économiser avec notre solution LED financée à 1€
              </motion.p>
            </div>

            {/* Colonne droite - Actions */}
            <div className="space-y-2 sm:space-y-3">
              {/* Points de confiance en bulles */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-3 sm:mb-4"
              >
                <motion.div 
                  className="flex items-center gap-1.5 sm:gap-2 bg-slate-700/50 backdrop-blur-sm border border-slate-600/50 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 hover:bg-slate-700/70 transition-all"
                  whileHover={window.innerWidth >= 768 ? { scale: 1.05, y: -2 } : {}}
                  transition={{ duration: 0.2 }}
                >
                  <motion.div 
                    className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-emerald-500 flex items-center justify-center"
                    animate={window.innerWidth >= 768 ? { 
                      scale: [1, 1.2, 1],
                      rotate: [0, 10, -10, 0]
                    } : {}}
                    transition={window.innerWidth >= 768 ? { 
                      duration: 2,
                      repeat: Infinity,
                      repeatDelay: 3
                    } : {}}
                  >
                    <i className="ri-check-line text-white text-xs sm:text-sm"></i>
                  </motion.div>
                  <span className="text-[10px] sm:text-xs text-white font-medium whitespace-nowrap">Installation dès 1€</span>
                </motion.div>

                <motion.div 
                  className="flex items-center gap-1.5 sm:gap-2 bg-slate-700/50 backdrop-blur-sm border border-slate-600/50 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 hover:bg-slate-700/70 transition-all"
                  whileHover={window.innerWidth >= 768 ? { scale: 1.05, y: -2 } : {}}
                  transition={{ duration: 0.2 }}
                >
                  <motion.div 
                    className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-emerald-500 flex items-center justify-center"
                    animate={window.innerWidth >= 768 ? { 
                      rotate: [0, 360],
                      scale: [1, 1.1, 1]
                    } : {}}
                    transition={window.innerWidth >= 768 ? { 
                      duration: 3,
                      repeat: Infinity,
                      repeatDelay: 2
                    } : {}}
                  >
                    <i className="ri-time-line text-white text-xs sm:text-sm"></i>
                  </motion.div>
                  <span className="text-[10px] sm:text-xs text-white font-medium whitespace-nowrap">Réponse en 24h</span>
                </motion.div>

                <motion.div 
                  className="flex items-center gap-1.5 sm:gap-2 bg-slate-700/50 backdrop-blur-sm border border-slate-600/50 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 hover:bg-slate-700/70 transition-all"
                  whileHover={window.innerWidth >= 768 ? { scale: 1.05, y: -2 } : {}}
                  transition={{ duration: 0.2 }}
                >
                  <motion.div 
                    className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-emerald-500 flex items-center justify-center"
                    animate={window.innerWidth >= 768 ? { 
                      y: [0, -3, 0],
                      scale: [1, 1.15, 1]
                    } : {}}
                    transition={window.innerWidth >= 768 ? { 
                      duration: 2.5,
                      repeat: Infinity,
                      repeatDelay: 4
                    } : {}}
                  >
                    <i className="ri-shield-check-line text-white text-xs sm:text-sm"></i>
                  </motion.div>
                  <span className="text-[10px] sm:text-xs text-white font-medium whitespace-nowrap">Garantie 5 ans</span>
                </motion.div>
              </motion.div>

              <motion.div 
                className="space-y-2 sm:space-y-3"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                {/* Bouton principal */}
                <a
                  href="/etude-gratuite"
                  className="group block w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-bold transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/30 hover:scale-105 text-center cursor-pointer whitespace-nowrap"
                >
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-flashlight-line"></i>
                    Demander mon étude gratuite
                    <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                  </span>
                </a>
              </motion.div>

              {/* Message de confiance compact */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="flex items-center justify-center gap-2 sm:gap-3 text-[10px] sm:text-xs text-gray-400 mt-1 sm:mt-2"
              >
                <span className="flex items-center gap-1">
                  <i className="ri-check-fill text-emerald-400"></i>
                  Sans engagement
                </span>
                <span className="text-gray-300">•</span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-fill text-emerald-400"></i>
                  Réponse rapide
                </span>
                <span className="text-gray-300">•</span>
                <span className="flex items-center gap-1">
                  <i className="ri-medal-fill text-emerald-400"></i>
                  Experts certifiés
                </span>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
